/**
 * Cart Line Item Reference Field for Shopify Horizon Theme V13
 * https://claude.ai/chat/2da59e44-cb1a-42fc-ad37-c6d1650afa54
 *
 * Adds an editable "Your Reference" input, Part Number (SKU),
 * and Line # label to each cart line item. Works with Horizon's
 * morphSection rendering by re-enhancing after each cart update.
 *
 * Usage: Add to assets/ and load via snippets/cart-line-reference.liquid
 */

const PROPERTY_KEY   = 'Your Reference';
const SECTION_ID_ATTR = 'data-section-id';
const INPUT_SELECTOR  = '.cart-ref-input';
const SKU_SELECTOR    = '.cart-ref-sku';
const LINE_NUM_SELECTOR = '.cart-ref-line-num';

/** @type {Object|null} Cached cart data */
let cachedCart = null;

/** @type {Promise<void>|null} Tracks pending cache updates from fetch interceptor */
let cacheUpdatePromise = null;

// ─── Cart API ─────────────────────────────────────────────────────────────────

/**
 * Fetches the current cart JSON and updates cache.
 * @returns {Promise<Object>}
 */
async function getCart() {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
  cachedCart = await res.json();
  return cachedCart;
}

/**
 * Updates a line item's properties via /cart/change.js.
 * Uses `line` param (1-based index) to avoid the variant ID silent-fail bug.
 *
 * @param {number} line - 1-based line index
 * @param {number} quantity - current quantity (must be included)
 * @param {Object} properties - full properties object (overwrites existing)
 * @param {string} sectionId - Horizon cart section ID for bundled rendering
 * @returns {Promise<Object>}
 */
async function updateLineProperties(line, quantity, properties, sectionId) {
  const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      line,
      quantity,
      properties,
      sections: sectionId,
      sections_url: window.location.pathname,
    }),
  });
  if (!res.ok) throw new Error(`Failed to update cart: ${res.status}`);
  const data = await res.json();
  cachedCart = data;
  return data;
}

// ─── DOM Helpers ──────────────────────────────────────────────────────────────

/**
 * Gets the section ID from the cart-items-component element.
 * @returns {string|null}
 */
function getSectionId() {
  const component = document.querySelector(`cart-items-component[${SECTION_ID_ATTR}]`);
  return component?.getAttribute(SECTION_ID_ATTR) || null;
}

/**
 * Reads the quantity for a line item directly from the DOM.
 * Falls back to cached cart data.
 * @param {number} line - 1-based line index
 * @returns {number}
 */
function getQuantityFromDOM(line) {
  const input = document.querySelector(
    `cart-quantity-selector-component input[data-cart-line="${line}"]`
  );
  if (input) {
    const val = parseInt(input.value, 10);
    if (!isNaN(val) && val > 0) return val;
  }
  return cachedCart?.items?.[line - 1]?.quantity || 1;
}

// ─── Cache Helpers ────────────────────────────────────────────────────────────

/**
 * Matches a DOM row to its cart item by comparing data-key attributes.
 * Key-based matching (not index-based) is critical — indexes shift when
 * items are removed, keys are stable for the lifetime of a line item.
 *
 * @param {HTMLElement} row
 * @returns {Object|null}
 */
function findCartItemForRow(row) {
  const domKey = row.getAttribute('data-key');
  if (!domKey || !cachedCart?.items) return null;
  return cachedCart.items.find((item) => item.key === domKey) || null;
}

/**
 * Gets the 1-based line index for a row by key-matching against cache.
 * @param {HTMLElement} row
 * @returns {number} 1-based index, or 0 if not found
 */
function getLineIndexForRow(row) {
  const domKey = row.getAttribute('data-key');
  if (!domKey || !cachedCart?.items) return 0;
  const index = cachedCart.items.findIndex((item) => item.key === domKey);
  return index >= 0 ? index + 1 : 0;
}

/**
 * Validates cache freshness by comparing the row's data-key against
 * the cached item key at the same DOM position. If mismatched (stale cache
 * from another tab or app), fetches fresh data.
 *
 * Uses key-based lookup — not index-based — to avoid false positives
 * when DOM order and cache order diverge.
 *
 * @param {HTMLElement} row - The row element being saved
 * @returns {Promise<boolean>} true if cache was already valid
 */
async function ensureCacheFresh(row) {
  const domKey = row.getAttribute('data-key');
  if (!domKey) return true;

  // Key-based: find matching item in cache regardless of position
  const cachedItem = cachedCart?.items?.find((item) => item.key === domKey);

  if (cachedItem) return true; // Key found in cache — cache is valid

  // Key not found — cart changed externally, refresh
  console.info('[Cart Reference] Cache mismatch detected, refreshing cart data');
  await getCart();
  return false;
}

// ─── Enhancement ──────────────────────────────────────────────────────────────

/**
 * Builds a reference input wrapper element.
 *
 * @param {string} rowKey - data-key of the row for stable identification
 * @param {number} lineIndex - 1-based line index for /cart/change.js
 * @param {string} currentValue - current reference value
 * @returns {HTMLElement}
 */
function createReferenceInput(rowKey, lineIndex, currentValue) {
  const wrapper = document.createElement('div');
  wrapper.className = 'cart-items__properties cart-ref-wrapper';
  wrapper.innerHTML = `
    <dt class="cart-ref-label">Your Reference:</dt>
    <dd class="cart-ref-dd">
      <input
        type="text"
        class="cart-ref-input"
        data-line="${lineIndex}"
        data-row-key="${rowKey}"
        value="${currentValue.replace(/"/g, '&quot;')}"
        placeholder=""
        autocomplete="off"
      >
      <span class="cart-ref-status" aria-live="polite"></span>
    </dd>
  `;
  return wrapper;
}

/**
 * Main enhancement function. For each cart line item row:
 *   1. Injects Line # label above the product title
 *   2. Injects Part Number (SKU) after variant options
 *   3. Replaces static "Your Reference" property text with an editable input,
 *      or injects a new input if no property exists yet
 *
 * Uses data-key matching (not array index) to map rows to cart items.
 *
 * @param {Object} [cartData] - optional pre-fetched cart JSON
 */
async function enhanceCartRows(cartData) {
  const rows = document.querySelectorAll(
    'cart-items-component .cart-items__table-row[data-key]'
  );
  if (!rows.length) return;

  if (cartData) {
    cachedCart = cartData;
  } else if (!cachedCart) {
    await getCart();
  }

  rows.forEach((row) => {
    const cartItem = findCartItemForRow(row);
    if (!cartItem) return;

    const lineIndex = getLineIndexForRow(row);
    if (!lineIndex) return;

    const rowKey = row.getAttribute('data-key') || '';
    const currentRef = (cartItem.properties?.[PROPERTY_KEY] || '').trim();
    const detailsCell = row.querySelector('.cart-items__details');
    if (!detailsCell) return;

    // ── 1. Line # label ──────────────────────────────────────────────────────
    if (!detailsCell.querySelector(LINE_NUM_SELECTOR)) {
      const label = document.createElement('div');
      label.className = 'cart-ref-line-num';
      label.textContent = `Line ${lineIndex}`;
      // Insert before the first child of detailsCell (above the title)
      detailsCell.insertBefore(label, detailsCell.firstChild);
    }

    // ── 2. Part Number (SKU) ─────────────────────────────────────────────────
    // Skip if already injected
    if (!row.querySelector(SKU_SELECTOR)) {
      const sku = cartItem.sku;
      if (sku) {
        const dl = detailsCell.querySelector('.cart-items__variants');
        if (dl) {
          const skuDiv = document.createElement('div');
          skuDiv.className = 'cart-items__variant cart-ref-sku';
          skuDiv.innerHTML = `<dt>Part# </dt><dd>${sku}</dd>`;

          // Insert before the existing "Your Reference" property if present,
          // otherwise append — ensures Part # always sits above Your Reference
          const existingProps = dl.querySelectorAll('.cart-items__properties');
          let refPropDiv = null;
          existingProps.forEach((div) => {
            const dt = div.querySelector('dt');
            if (dt && dt.textContent.trim().replace(':', '') === PROPERTY_KEY) {
              refPropDiv = div;
            }
          });

          if (refPropDiv) {
            dl.insertBefore(skuDiv, refPropDiv);
          } else {
            dl.appendChild(skuDiv);
          }
        }
      }
    }

    // ── 3. Your Reference input ──────────────────────────────────────────────
    // Skip if already enhanced
    if (row.querySelector(INPUT_SELECTOR)) return;

    // Check if Horizon rendered a static "Your Reference" property
    const existingPropertyDivs = row.querySelectorAll('.cart-items__properties');
    let existingRefDiv = null;
    existingPropertyDivs.forEach((div) => {
      const dt = div.querySelector('dt');
      if (dt && dt.textContent.trim().replace(':', '') === PROPERTY_KEY) {
        existingRefDiv = div;
      }
    });

    if (existingRefDiv) {
      // Replace static <dd> text with an editable input
      const dd = existingRefDiv.querySelector('dd');
      if (dd) {
        dd.innerHTML = `
          <input
            type="text"
            class="cart-ref-input"
            data-line="${lineIndex}"
            data-row-key="${rowKey}"
            value="${currentRef.replace(/"/g, '&quot;')}"
            placeholder=""
            autocomplete="off"
          >
          <span class="cart-ref-status" aria-live="polite"></span>
        `;
        dd.className = 'cart-ref-dd';
        existingRefDiv.classList.add('cart-ref-wrapper');
        const dt = existingRefDiv.querySelector('dt');
        if (dt) dt.className = 'cart-ref-label';
      }
    } else {
      // No existing property — inject a new reference field
      const dl = detailsCell.querySelector('.cart-items__variants');
      const refInput = createReferenceInput(rowKey, lineIndex, currentRef);
      if (dl) {
        dl.appendChild(refInput);
      } else {
        const priceDiv = detailsCell.querySelector('div');
        if (priceDiv) {
          detailsCell.insertBefore(refInput, priceDiv);
        } else {
          detailsCell.appendChild(refInput);
        }
      }
    }
  });
}

// ─── Save Reference ───────────────────────────────────────────────────────────

/**
 * Saves the reference value for a line item.
 *
 * Flow:
 *   1. Resolve line index from current DOM order (not stale data-line attr)
 *   2. Validate cache freshness via key-based lookup
 *   3. Skip save if value unchanged
 *   4. POST to /cart/change.js with merged properties (single request)
 *   5. Morph section via Horizon's morphSection utility
 *   6. Re-enhance rows with updated cart data
 *   7. Resolve shared savePromise so share-cart.js can proceed
 *
 * @param {HTMLInputElement} input
 */
async function saveReference(input) {
  if (input.dataset.saving === 'true') return;

  const rowKey = input.dataset.rowKey;
  if (!rowKey) return;

  const newValue = input.value.trim();
  const sectionId = getSectionId();
  if (!sectionId) return;

  const row = input.closest('.cart-items__table-row');
  if (!row) return;

  // Validate cache using key-based lookup (not index-based)
  await ensureCacheFresh(row);

  // Resolve cart item and line index from cache via key matching
  const cartItem = findCartItemForRow(row);
  if (!cartItem) return;
  const line = getLineIndexForRow(row);
  if (!line) return;

  // Skip if value unchanged
  const existingRef = (cartItem.properties?.[PROPERTY_KEY] || '').trim();
  if (newValue === existingRef) return;

  const statusEl = input.parentElement?.querySelector('.cart-ref-status');

  // Expose save promise on shared namespace so share-cart.js can await it
  let resolveSave;
  const savePromise = new Promise((resolve) => { resolveSave = resolve; });
  window.__hpsCart = window.__hpsCart || {};
  window.__hpsCart.savePromise = savePromise;

  try {
    input.dataset.saving = 'true';

    if (statusEl) {
      statusEl.textContent = 'Saving...';
      statusEl.className = 'cart-ref-status cart-ref-status--saving';
    }
    input.classList.add('cart-ref-input--saving');

    // Read quantity from DOM — no extra network request
    const quantity = getQuantityFromDOM(line);

    // Merge reference into existing properties
    const mergedProperties = { ...cartItem.properties };
    mergedProperties[PROPERTY_KEY] = newValue || ' ';

    // Single POST — includes sections param for bundled section HTML
    const updatedCart = await updateLineProperties(
      line,
      quantity,
      mergedProperties,
      sectionId
    );

    // Show saved immediately — don't wait for morph
    if (statusEl) {
      statusEl.textContent = '✓ Saved';
      statusEl.className = 'cart-ref-status cart-ref-status--saved';
    }
    input.classList.remove('cart-ref-input--saving');
    input.dataset.saving = 'false';

    // Morph section using Horizon's utility directly
    const sectionHtml = updatedCart.sections?.[sectionId];
    if (sectionHtml) {
      const { morphSection } = await import('@theme/section-renderer');
      morphSection(sectionId, sectionHtml);
    }

    // Re-enhance after morph, then show saved status on the correct row.
    // Match by rowKey (via the new cart's item keys) not by value — avoids
    // the duplicate-value ambiguity bug.
    requestAnimationFrame(() => {
      enhanceCartRows(updatedCart).then(() => {
        // After saving, the item's key changes (properties changed).
        // Find the new key by locating the item whose previous key matches
        // via position — the item is still at the same index in updatedCart.
        const savedIndex = cachedCart?.items?.findIndex(
          (item) => item.properties?.[PROPERTY_KEY]?.trim() === (newValue || ' ').trim()
            && item.variant_id === cartItem.variant_id
        );

        if (savedIndex >= 0) {
          const newKey = cachedCart.items[savedIndex].key;
          const newInput = document.querySelector(
            `${INPUT_SELECTOR}[data-row-key="${newKey}"]`
          );
          if (newInput) {
            const newStatus = newInput.parentElement?.querySelector('.cart-ref-status');
            if (newStatus) {
              newStatus.textContent = '✓ Saved';
              newStatus.className = 'cart-ref-status cart-ref-status--saved';
              setTimeout(() => {
                newStatus.textContent = '';
                newStatus.className = 'cart-ref-status';
              }, 1500);
            }
          }
        }

        resolveSave();
        if (window.__hpsCart) window.__hpsCart.savePromise = null;
      });
    });

  } catch (err) {
    console.error('[Cart Reference]', err);
    if (statusEl) {
      statusEl.textContent = 'Error saving';
      statusEl.className = 'cart-ref-status cart-ref-status--error';
    }
    input.classList.remove('cart-ref-input--saving');
    input.dataset.saving = 'false';
    resolveSave();
    if (window.__hpsCart) window.__hpsCart.savePromise = null;
  }
}

// ─── Event Handlers ───────────────────────────────────────────────────────────

function handleBlurEvent(e) {
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('cart-ref-input')) return;
  saveReference(input);
}

function handleKeydownEvent(e) {
  if (e.key !== 'Enter') return;
  const input = e.target;
  if (!(input instanceof HTMLInputElement) || !input.classList.contains('cart-ref-input')) return;
  input.blur();
}

// ─── Fetch Interceptor ────────────────────────────────────────────────────────

/**
 * Intercepts fetch to capture cart data from Horizon's own requests.
 * Stores a promise so the MutationObserver can await the cache update,
 * preventing the race condition between morph completion and cache freshness.
 */
function installFetchInterceptor() {
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);

    try {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const isCartMutation =
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/add') ||
        url.includes('/cart/clear');

      if (isCartMutation) {
        const clone = response.clone();
        cacheUpdatePromise = clone.json().then((data) => {
          if (data?.items) cachedCart = data;
        }).catch(() => {});
      }
    } catch {
      // Never break the original fetch
    }

    return response;
  };
}

// ─── Morph Observer ───────────────────────────────────────────────────────────

/**
 * Observes cart-items-component for DOM changes from Horizon's own
 * morphSection calls (quantity changes, removals, discounts).
 *
 * Awaits any pending interceptor cache update before re-enhancing
 * to prevent stale data races. Uses microtask batching to avoid
 * running enhanceCartRows multiple times per morph cycle.
 *
 * cacheUpdatePromise is explicitly reset after consumption to prevent
 * stale promise references on subsequent morph cycles.
 */
function observeCartMorphs() {
  const cartComponent = document.querySelector('cart-items-component');
  if (!cartComponent) return;

  let morphPending = false;

  const observer = new MutationObserver(() => {
    if (morphPending) return;

    const hasInputs = cartComponent.querySelector(INPUT_SELECTOR);
    if (!hasInputs) {
      morphPending = true;
      queueMicrotask(async () => {
        try {
          // Await and then clear the pending interceptor update
          if (cacheUpdatePromise) {
            await cacheUpdatePromise;
            cacheUpdatePromise = null; // Reset to prevent stale reference
          }

          if (cachedCart) {
            await enhanceCartRows(cachedCart);
          } else {
            const freshCart = await getCart();
            await enhanceCartRows(freshCart);
          }
        } catch (err) {
          console.error('[Cart Reference] Re-enhance failed:', err);
        } finally {
          morphPending = false;
        }
      });
    }
  });

  observer.observe(cartComponent, { childList: true, subtree: true });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

function init() {
  if (!document.querySelector('cart-items-component')) return;

  installFetchInterceptor();
  enhanceCartRows();

  document.addEventListener('blur', handleBlurEvent, true);
  document.addEventListener('keydown', handleKeydownEvent);

  observeCartMorphs();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}